import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';
import Logo from '../../assets/Logo.png';



const SupportContainer = styled.div`
  width: 100%;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  box-sizing: border-box;
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    padding: 10px;
    margin-left: 0; /* Reset for mobile default, let sidebar overlay */
  }

  /* Match Sidebar's fixed behavior on tall screens */
  @media (min-height: 850px) {
    margin-left: ${props => props.$isSidebarOpen ? '17.5rem' : '3.75rem'};
    width: auto; /* Allow it to take remaining space */
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 40px;
  color: #000;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 20px;
  }
`;

const ChatBox = styled.div`
  background: linear-gradient(135deg, #6b7fd7 0%, #8b6fd7 100%);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #6b7fd7 0%, #8b6fd7 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 2px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const HeaderStatus = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
`;

const ChatMessages = styled.div`
  background: white;
  padding: 24px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 15px;
    min-height: 300px;
    max-height: 500px;
    height: 60vh;
  }
`;

const Message = styled.div`
  display: flex;
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  animation: fadeIn 0.3s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MessageBubble = styled.div`
  background: ${props => props.$isUser ? '#6b7fd7' : '#f5f5f5'};
  color: ${props => props.$isUser ? 'white' : '#333'};
  padding: 12px 18px;
  border-radius: 18px;
  max-width: 70%;
  word-wrap: break-word;
  font-size: 15px;
  line-height: 1.5;

  @media (max-width: 768px) {
    max-width: 85%;
    padding: 10px 14px;
    font-size: 14px;
  }
`;

const UserLabel = styled.div`
  background: #6b7fd7;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  align-self: flex-end;
`;

const InputContainer = styled.div`
  background: white;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    padding: 15px;
    gap: 8px;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #6b7fd7;
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background: #6b7fd7;
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #5a6ec7;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 12px 18px;
  background: #f5f5f5;
  border-radius: 18px;
  width: fit-content;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #999;
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-10px);
    }
  }
`;

const API_ENDPOINT = 'https://flowproai.aivoria.nl/ai_flow_pro/query_multidoc';

export default function Support() {
  const context = useOutletContext();
  const isSidebarOpen = context ? context.isSidebarOpen : true;

  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'ASSISTANT',
      content: 'Hoi, dit is jouw support chat. Hoe kan ik je helpen?',
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (inputValue.trim() && !isLoading) {
      const userMessage = inputValue.trim();

      // Clear input immediately
      setInputValue('');

      // Add user message to the chat
      const userMsg = {
        id: Date.now(),
        role: 'USER',
        content: userMessage
      };

      setMessages(prev => [...prev, userMsg]);

      // Send message to API
      try {
        setIsLoading(true);

        // Build the API messages array (need to include the new user message)
        const apiMessages = messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        // Add the new user message
        apiMessages.push({
          role: 'USER',
          content: userMessage
        });

        const payload = {
          user_id: 'aa-support',
          user_name: 'AiFlowPro',
          language: 'dutch',
          messages: apiMessages
        };

        const response = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error('Failed to get response from server');
        }

        const data = await response.json();

        // Add assistant's response to messages
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + 1,
            role: 'ASSISTANT',
            content: data.content || data.response || data.message || 'Sorry, I could not process your request.'
          }
        ]);
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + 1,
            role: 'ASSISTANT',
            content: 'Sorry, there was an error processing your request. Please try again.'
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSend();
    }
  };

  return (
    <SupportContainer $isSidebarOpen={isSidebarOpen}>
      <Title>Support Chat</Title>

      <ChatBox>
        <ChatHeader>
          <Avatar>
            <img src={Logo} alt="Aivoria Support" />
          </Avatar>
          <HeaderInfo>
            <HeaderName>Aivoria AA</HeaderName>
            <HeaderStatus>Support Assistent</HeaderStatus>
          </HeaderInfo>
        </ChatHeader>

        <ChatMessages>
          {messages.map((message) => (
            <Message key={message.id} $isUser={message.role === 'USER'}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: message.role === 'USER' ? 'flex-end' : 'flex-start' }}>
                {/* {message.role === 'USER' && <UserLabel>Hi</UserLabel>} */}
                <MessageBubble $isUser={message.role === 'USER'}>
                  {message.content}
                </MessageBubble>
              </div>
            </Message>
          ))}

          {isLoading && (
            <Message $isUser={false}>
              <TypingIndicator>
                <span></span>
                <span></span>
                <span></span>
              </TypingIndicator>
            </Message>
          )}

          <div ref={messagesEndRef} />
        </ChatMessages>

        <InputContainer>
          <Input
            type="text"
            placeholder="Typ hier bericht"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <SendButton onClick={handleSend} disabled={isLoading || !inputValue.trim()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </SendButton>
        </InputContainer>
      </ChatBox>
    </SupportContainer>
  );
}